import React, { useState } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Badge from '../components/common/Badge';
import Table from '../components/common/Table';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { FileText, Download, Eye, ArrowLeft, Plus, Calendar, Users, Hash } from 'lucide-react';
import { formatDate, delay } from '../utils/helpers';
import toast from 'react-hot-toast';

const ContratosListPage = () => {
  const { mockData } = useAuth();
  const navigate = useNavigate();

  const contratos = mockData?.contratos || [];

  const estadoCounts = contratos.reduce((acc, c) => {
    acc[c.estado] = (acc[c.estado] || 0) + 1;
    return acc;
  }, {});

  const columns = [
    {
      header: 'Nº Contrato',
      cell: (row) => (
        <span className="font-mono text-sm font-semibold text-gray-900">{row.numeroContrato}</span>
      )
    },
    {
      header: 'Tipo',
      cell: (row) => (
        <span className="text-sm text-gray-700">{row.tipo}</span>
      )
    },
    {
      header: 'Cliente',
      accessor: 'partes.cliente'
    },
    {
      header: 'Fecha Inicio',
      cell: (row) => (
        <span className="text-sm text-gray-700">{formatDate(row.fechaInicio)}</span>
      )
    },
    {
      header: 'Fecha Fin',
      cell: (row) => (
        <span className="text-sm text-gray-700">{formatDate(row.fechaFin)}</span>
      )
    },
    {
      header: 'Estado',
      cell: (row) => (
        <Badge variant={row.estado === 'vigente' ? 'success' : 'default'}>
          {row.estado.toUpperCase()}
        </Badge>
      )
    },
    {
      header: 'Versiones',
      cell: (row) => (
        <span className="text-sm text-gray-700">{row.versiones}</span>
      )
    },
    {
      header: 'Acciones',
      cell: (row) => (
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            icon={Eye}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/contratos/${row.id}`);
            }}
          >
            Ver
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={Download}
            onClick={(e) => {
              e.stopPropagation();
              toast.success('Descargando PDF...');
            }}
          >
            PDF
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Contratos</h1>
          <p className="text-sm text-gray-500 mt-1">Gestione contratos, anexos y documentos asociados</p>
        </div>
        <Button
          variant="primary"
          icon={Plus}
          onClick={() => toast.success('Funcionalidad en desarrollo')}
        >
          Nuevo Contrato
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center">
              <FileText className="h-5 w-5 text-primary-700" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Contratos</p>
              <p className="text-xl font-semibold text-gray-900">{contratos.length}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-success-50 rounded-lg flex items-center justify-center">
              <FileText className="h-5 w-5 text-success-700" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Vigentes</p>
              <p className="text-xl font-semibold text-gray-900">{estadoCounts['vigente'] || 0}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
              <FileText className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Otros Estados</p>
              <p className="text-xl font-semibold text-gray-900">
                {contratos.length - (estadoCounts['vigente'] || 0)}
              </p>
            </div>
          </div>
        </Card>
      </div>

      <Card>
        <Table
          columns={columns}
          data={contratos}
          onRowClick={(row) => navigate(`/contratos/${row.id}`)}
        />
      </Card>
    </div>
  );
};

const ContratoDetallePage = () => {
  const { id } = useParams();
  const { mockData } = useAuth();
  const navigate = useNavigate();
  const [contrato, setContrato] = useState(null);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    const loadContrato = async () => {
      await delay(300);
      const found = mockData?.contratos?.find(c => c.id === id);
      setContrato(found);
      setLoading(false);
    };
    loadContrato();
  }, [id, mockData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!contrato) {
    return (
      <div className="space-y-6">
        <div className="text-center py-16">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Contrato no encontrado</h2>
          <p className="text-sm text-gray-500 mb-6">No se pudo encontrar el contrato solicitado.</p>
          <Button variant="outline" onClick={() => navigate('/contratos')}>
            Volver al listado
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="sm" icon={ArrowLeft} onClick={() => navigate('/contratos')}>
            Volver
          </Button>
          <div className="border-l border-gray-200 pl-4">
            <h1 className="text-2xl font-semibold text-gray-900">Contrato {contrato.numeroContrato}</h1>
            <p className="text-sm text-gray-500 mt-0.5">Detalle completo del contrato</p>
          </div>
        </div>
        <Badge size="lg" variant={contrato.estado === 'vigente' ? 'success' : 'default'}>
          {contrato.estado.toUpperCase()}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card title="Informacion General">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mt-0.5">
                  <Hash className="h-4 w-4 text-gray-500" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Numero de Contrato</p>
                  <p className="text-sm font-semibold text-gray-900 font-mono mt-0.5">{contrato.numeroContrato}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mt-0.5">
                  <FileText className="h-4 w-4 text-gray-500" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Tipo</p>
                  <p className="text-sm font-semibold text-gray-900 mt-0.5">{contrato.tipo}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mt-0.5">
                  <Calendar className="h-4 w-4 text-gray-500" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Fecha de Inicio</p>
                  <p className="text-sm font-semibold text-gray-900 mt-0.5">{formatDate(contrato.fechaInicio)}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mt-0.5">
                  <Calendar className="h-4 w-4 text-gray-500" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Fecha de Fin</p>
                  <p className="text-sm font-semibold text-gray-900 mt-0.5">{formatDate(contrato.fechaFin)}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mt-0.5">
                  <FileText className="h-4 w-4 text-gray-500" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Version</p>
                  <p className="text-sm font-semibold text-gray-900 mt-0.5">{contrato.versiones}</p>
                </div>
              </div>
            </div>
          </Card>

          <Card title="Partes del Contrato">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-primary-50 rounded-lg flex items-center justify-center mt-0.5">
                  <Users className="h-4 w-4 text-primary-700" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Cliente</p>
                  <p className="text-sm font-semibold text-gray-900 mt-0.5">{contrato.partes.cliente}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-primary-50 rounded-lg flex items-center justify-center mt-0.5">
                  <Users className="h-4 w-4 text-primary-700" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Proveedor</p>
                  <p className="text-sm font-semibold text-gray-900 mt-0.5">{contrato.partes.proveedor}</p>
                </div>
              </div>
            </div>
          </Card>

          {contrato.clausulas && contrato.clausulas.length > 0 && (
            <Card title="Clausulas">
              <div className="space-y-3">
                {contrato.clausulas.map((clausula, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-1">{clausula.titulo}</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">{clausula.descripcion}</p>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card title="Acciones">
            <div className="space-y-2">
              <Button
                variant="primary"
                className="w-full"
                icon={Download}
                onClick={() => toast.success('Descargando contrato...')}
              >
                Descargar PDF
              </Button>
              <Button
                variant="outline"
                className="w-full"
                icon={FileText}
                onClick={() => toast.success('Generando anexo...')}
              >
                Generar Anexo
              </Button>
            </div>
          </Card>

          <Card title="Documentos">
            <div className="space-y-2">
              {contrato.documentos && contrato.documentos.length > 0 ? (
                contrato.documentos.map((doc, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                        <FileText className="h-4 w-4 text-gray-500" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{doc.nombre}</p>
                        <p className="text-xs text-gray-500">{doc.tipo}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={Download}
                      onClick={() => toast.success('Descargando documento...')}
                    />
                  </div>
                ))
              ) : (
                <div className="text-center py-6">
                  <FileText className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No hay documentos adjuntos</p>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

const ContratosPage = () => {
  return (
    <Layout>
      <Routes>
        <Route index element={<ContratosListPage />} />
        <Route path=":id" element={<ContratoDetallePage />} />
      </Routes>
    </Layout>
  );
};

export default ContratosPage;
